import { stringUtils } from '@festina-life/core'
import type { ReactNode } from 'react'
import { useMemo } from 'react'

import { defaultKeyFn, defaultRenderFn } from './RawSelect.utils'
import { useResolvedRenderedValues } from './useResolvedRenderedValues'

export type SelectableItem<T> = {
  key: string
  value: T
  renderedOption: ReactNode
  renderedInputValue: ReactNode
  /**
   * Resolved plain text of the item's input value. Used for filtering,
   * accessibility textValue, and setting the input when an item is selected.
   * For async renderers this is populated after the hidden mirror resolves.
   */
  textValue: string
}

export const useSelectableItems = <T>({
  options,
  renderFn = defaultRenderFn,
  renderValueFn,
  keyFn = defaultKeyFn,
  skipOptionResolution = false,
}: {
  options: readonly T[]
  renderFn?: (item: T) => ReactNode
  renderValueFn?: (item: T) => ReactNode
  keyFn?: (item: T) => string
  skipOptionResolution?: boolean
}) => {
  const rawItems = useMemo(
    () =>
      options.map((option) => {
        const renderedOption = renderFn(option)
        const renderedInputValue = renderValueFn?.(option) ?? renderedOption
        const textValue = skipOptionResolution
          ? keyFn(option)
          : stringUtils.reactNodeToDisplayText(renderedInputValue)
        return {
          key: keyFn(option),
          value: option,
          renderedOption,
          renderedInputValue,
          textValue,
        }
      }),
    [options, renderFn, renderValueFn, keyFn, skipOptionResolution],
  )

  const { resolvedRenderedValues, extractionMirrors } =
    useResolvedRenderedValues({
      options: rawItems,
      enabled: !skipOptionResolution,
    })

  // Merge async-resolved text back into each item so consumers only need to
  // read item.textValue regardless of whether the renderer is sync or async.
  const items: SelectableItem<T>[] = useMemo(
    () =>
      skipOptionResolution
        ? rawItems
        : rawItems.map((item) => ({
            ...item,
            textValue: item.textValue || resolvedRenderedValues[item.key] || '',
          })),
    [rawItems, resolvedRenderedValues, skipOptionResolution],
  )

  return { items, extractionMirrors }
}
